if (!customElements.get("reviews-controller")) {
  class ReviewsController extends HTMLElement {
    connectedCallback() {
      this.filterButtons = Array.from(this.querySelectorAll("[data-reviews-filter]"));
      this.prevBtn = this.querySelector("[data-reviews-prev]");
      this.nextBtn = this.querySelector("[data-reviews-next]");

      // Desktop: Swiper cards
      this.reviewItems = Array.from(
        this.querySelectorAll("[data-reviews-desktop] [data-tags]")
      );

      // Mobile: stacked list
      this.mobileContainer = this.querySelector("[data-reviews-mobile]");
      this.mobileCards = this.mobileContainer
        ? Array.from(this.mobileContainer.querySelectorAll("[data-tags]"))
        : [];
      this.loadMoreBtn = this.querySelector("[data-reviews-load-more]");
      this.mobileVisibleCount = 3;

      // Filter pill handlers
      this.filterButtons.forEach((button) => {
        button.addEventListener("click", () => {
          if (button.classList.contains("reviews-tag-selected")) {
            button.classList.remove("reviews-tag-selected");
          } else {
            this.filterButtons.forEach((btn) =>
              btn.classList.remove("reviews-tag-selected")
            );
            button.classList.add("reviews-tag-selected");
          }
          this.filterReviews();
          if (this.isSticky) {
            this.scrollBackToReviews();
          }
        });
      });

      // Load more handler
      if (this.loadMoreBtn) {
        this.loadMoreBtn.addEventListener("click", () => {
          this.mobileVisibleCount = Infinity;
          this.updateMobileVisibility();
        });
      }

      // Set initial mobile state (show first 3)
      this.updateMobileVisibility();

      this.initCarousel();
      this.initStickyPills();
    }

    initCarousel() {
      const carousel = this.querySelector("site-carousel");
      if (!carousel) return;

      const ready = () => {
        if (!carousel.swiper) {
          requestAnimationFrame(ready);
          return;
        }

        if (this.prevBtn && this.nextBtn) {
          this.prevBtn.addEventListener("click", () => carousel.swiper.slidePrev());
          this.nextBtn.addEventListener("click", () => carousel.swiper.slideNext());
          carousel.swiper.on("slideChange", () => this.updateNavState());
          carousel.swiper.on("resize", () => this.updateNavState());
        }

        this.updateNavState();
      };

      requestAnimationFrame(ready);
    }

    updateNavState() {
      const carousel = this.querySelector("site-carousel");
      if (!carousel || !carousel.swiper) return;
      if (this.prevBtn) this.prevBtn.disabled = carousel.swiper.isBeginning;
      if (this.nextBtn) this.nextBtn.disabled = carousel.swiper.isEnd;
    }

    getSelectedReviewsFilter() {
      return (
        this.filterButtons
          .find((button) => button.classList.contains("reviews-tag-selected"))
          ?.getAttribute("data-reviews-filter") || null
      );
    }

    filterReviews() {
      const selectedFilter = this.getSelectedReviewsFilter();

      // Desktop: filter Swiper slides
      this.reviewItems.forEach((item) => {
        const itemTags = item.getAttribute("data-tags").split(",");
        const shouldShow = !selectedFilter || itemTags.includes(selectedFilter);
        const swiperSlide = item.closest(".swiper-slide");
        if (swiperSlide) {
          swiperSlide.style.display = shouldShow ? "" : "none";
        }
      });

      // Mobile: reset to first 3 and refilter
      this.mobileVisibleCount = 3;
      this.updateMobileVisibility();

      // Update Swiper
      const carousel = this.querySelector("site-carousel");
      if (carousel && carousel.swiper) {
        carousel.swiper.update();
        carousel.swiper.slideTo(0, 0);
        this.updateNavState();
      }
    }

    /* ── Mobile stacked list ── */

    updateMobileVisibility() {
      if (!this.mobileContainer) return;

      const selectedFilter = this.getSelectedReviewsFilter();
      let shownCount = 0;
      let totalMatching = 0;

      this.mobileCards.forEach((card) => {
        const itemTags = card.getAttribute("data-tags").split(",");
        const matchesFilter =
          !selectedFilter || itemTags.includes(selectedFilter);

        if (!matchesFilter) {
          card.style.display = "none";
          return;
        }

        totalMatching++;
        if (shownCount < this.mobileVisibleCount) {
          card.style.display = "";
          shownCount++;
        } else {
          card.style.display = "none";
        }
      });

      if (this.loadMoreBtn) {
        this.loadMoreBtn.style.display =
          shownCount < totalMatching ? "" : "none";
      }
    }

    /* ── Sticky pills (mobile only) ── */

    initStickyPills() {
      if (window.matchMedia("(min-width: 1024px)").matches) return;

      this.pillsContainer = this.querySelector("[data-reviews-pills]");
      if (!this.pillsContainer) return;

      this.isSticky = false;
      this.pillsOriginalTop = 0;

      // Placeholder prevents layout shift when pills go fixed
      this.pillsPlaceholder = document.createElement("div");
      this.pillsPlaceholder.style.display = "none";
      this.pillsContainer.parentNode.insertBefore(
        this.pillsPlaceholder,
        this.pillsContainer.nextSibling
      );

      // End boundary: bottom of the next shopify section (UGC carousel)
      const parentSection = this.closest(".shopify-section");
      this.stickyEndSection = parentSection?.nextElementSibling;

      this._onScroll = () => this.handleStickyScroll();
      this._onResize = () => {
        if (window.matchMedia("(min-width: 1024px)").matches) {
          if (this.isSticky) this.unstickPills();
          return;
        }
        if (this.isSticky) this.unstickPills();
        this.handleStickyScroll();
      };

      window.addEventListener("scroll", this._onScroll, { passive: true });
      window.addEventListener("resize", this._onResize, { passive: true });
    }

    getHeaderHeight() {
      const header =
        document.getElementById("shopify-section-header") ||
        document.querySelector(".shopify-section-header");
      return header ? header.offsetHeight : 0;
    }

    handleStickyScroll() {
      if (!this.pillsContainer) return;
      const scrollY = window.scrollY;
      const headerH = this.getHeaderHeight();

      let startY;
      if (this.isSticky) {
        startY = parseFloat(this.pillsPlaceholder.dataset.top);
      } else {
        const rect = this.pillsContainer.getBoundingClientRect();
        startY = rect.top + scrollY - headerH;
        this.pillsOriginalTop = startY;
      }

      let endY = Infinity;
      if (this.stickyEndSection) {
        const endRect = this.stickyEndSection.getBoundingClientRect();
        endY =
          endRect.bottom +
          scrollY -
          this.pillsContainer.offsetHeight -
          headerH;
      }

      if (scrollY >= startY && scrollY < endY) {
        if (!this.isSticky) this.stickPills();
      } else {
        if (this.isSticky) this.unstickPills();
      }
    }

    stickPills() {
      const el = this.pillsContainer;
      const headerH = this.getHeaderHeight();

      this.pillsPlaceholder.dataset.top = String(this.pillsOriginalTop);
      this.pillsPlaceholder.style.height = el.offsetHeight + "px";
      this.pillsPlaceholder.style.display = "block";

      el.style.position = "fixed";
      el.style.top = headerH + "px";
      el.style.left = "0";
      el.style.width = "100%";
      el.style.zIndex = "40";
      el.style.backgroundColor = "#E6E8E6";
      el.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
      el.style.paddingTop = "12px";
      el.style.paddingBottom = "12px";

      this.isSticky = true;
    }

    unstickPills() {
      const el = this.pillsContainer;
      this.pillsPlaceholder.style.display = "none";

      el.style.position = "";
      el.style.top = "";
      el.style.left = "";
      el.style.width = "";
      el.style.zIndex = "";
      el.style.backgroundColor = "";
      el.style.boxShadow = "";
      el.style.paddingTop = "";
      el.style.paddingBottom = "";

      this.isSticky = false;
    }

    scrollBackToReviews() {
      const targetY =
        parseFloat(this.pillsPlaceholder.dataset.top) ||
        this.pillsOriginalTop;
      window.scrollTo({ top: targetY, behavior: "smooth" });
    }
  }

  customElements.define("reviews-controller", ReviewsController);
}
